import {
  ALL_COMMUNITIES_FILTER,
  getAnnouncementCommentCount,
  getGroupKey,
  matchesCommunityFilter,
} from '../../utils/announcement';

import texts from './texts.json';

const DAY_MS = 24 * 60 * 60 * 1000;

const toMillis = (doc) => (doc.createdAt ? doc.createdAt.toMillis() : 0);

const ANNOUNCEMENT_DATE_RANGE = {
  SEVEN_DAYS: 7,
  TWO_WEEKS: 14,
  MONTH: 30,
  THREE_MONTHS: 90,
  ALL: 'ALL',
};

const DEFAULT_ANNOUNCEMENT_DATE_RANGE = ANNOUNCEMENT_DATE_RANGE.SEVEN_DAYS;

const ANNOUNCEMENT_DATE_RANGE_OPTIONS = [
  { value: ANNOUNCEMENT_DATE_RANGE.SEVEN_DAYS, label: texts.dateRange.SEVEN_DAYS },
  { value: ANNOUNCEMENT_DATE_RANGE.TWO_WEEKS, label: texts.dateRange.TWO_WEEKS },
  { value: ANNOUNCEMENT_DATE_RANGE.MONTH, label: texts.dateRange.MONTH },
  { value: ANNOUNCEMENT_DATE_RANGE.THREE_MONTHS, label: texts.dateRange.THREE_MONTHS },
  { value: ANNOUNCEMENT_DATE_RANGE.ALL, label: texts.dateRange.ALL },
];

/**
 * Lower bound Date for a range, or null for "All time" (no bound).
 * @param {number|'ALL'} range
 * @returns {Date|null}
 */
const getSinceDate = (range) => {
  if (range === ANNOUNCEMENT_DATE_RANGE.ALL) {
    return null;
  }
  return new Date(Date.now() - range * DAY_MS);
};

/**
 * True when the selected range is already covered by what has been fetched.
 * fetchedSinceDate === null means "All time" is already loaded.
 * @param {number|'ALL'} range
 * @param {Date|null} fetchedSinceDate
 * @returns {boolean}
 */
const isRangeCovered = (range, fetchedSinceDate) => {
  if (fetchedSinceDate === null) {
    return true;
  }
  const targetSince = getSinceDate(range);
  if (targetSince === null) {
    return false;
  }
  return targetSince.getTime() >= fetchedSinceDate.getTime();
};

/**
 * Build the display-ready feed: pinned docs first (date desc, never range-filtered),
 * then non-pinned docs within range (date desc), deduped against pinned.
 * @param {Array} dateDocs - flattened docs from the date-bounded segments
 * @param {Array} pinnedDocs - docs from the unbounded pinned collection
 * @param {Date|null} since - lower bound for non-pinned docs, null = no bound
 * @returns {Array}
 */
const mergeAnnouncementDocs = (dateDocs, pinnedDocs, since) => {
  const byCreatedDesc = (a, b) => toMillis(b) - toMillis(a);
  const pinnedIds = new Set(pinnedDocs.map((d) => d.id));
  const inRange = since
    ? dateDocs.filter((d) => d.createdAt && d.createdAt.toMillis() >= since.getTime())
    : dateDocs;
  const nonPinned = inRange.filter((d) => !pinnedIds.has(d.id) && !d.isPinned);
  return [
    ...pinnedDocs.slice().sort(byCreatedDesc),
    ...nonPinned.slice().sort(byCreatedDesc),
  ];
};

/**
 * Collapses each multi-community post's sibling docs into one display entry, so the coach's feed
 * shows a post once with its interactions summed across the communities it went to.
 *
 * @param {Array} docs - Display-ready announcements, already filtered and ordered.
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] - When false every doc becomes its own entry, which is
 * what the community-scoped feed wants: there, a post's own per-community counts are the truth.
 * @param {boolean} [options.preserveOrder=false] - Keep the order the docs arrived in instead of
 * re-sorting by pin then recency. The scheduled feed is fetched by `publishAt` asc, which is the
 * order that tab means to show; sorting by `createdAt` desc would scramble it.
 * @returns {Array} Entries of { key, base, siblings, isPinnedGroup, totalLikes, totalComments,
 * communityIds, missingSiblingCount }.
 */
const groupAnnouncementsByBasePost = (docs, { enabled = true, preserveOrder = false } = {}) => {
  const buckets = new Map();

  docs.forEach((doc) => {
    // Grouping off still produces entries, so the card has one shape to render either way.
    const key = enabled ? getGroupKey(doc) : doc.id;
    if (!buckets.has(key)) {
      buckets.set(key, []);
    }
    buckets.get(key).push(doc);
  });

  const entries = [...buckets.entries()].map(([key, group]) => {
    const siblings = group.slice().sort((a, b) => toMillis(a) - toMillis(b));
    // The base sibling can be missing from the loaded window - an edit may have removed it, or the
    // date range may not reach it - so fall back to the oldest sibling that did load.
    const base = siblings.find((doc) => doc.id === key) || siblings[0];
    // Prefer the stored id list: it still names communities whose siblings are not loaded, so the
    // chips stay complete even when the counts below are only partial.
    const communityIds = base.groupCommunityIds.length
      ? base.groupCommunityIds
      : siblings.map((doc) => doc.communityId).filter(Boolean);

    return {
      key,
      base,
      siblings,
      isPinnedGroup: siblings.some((doc) => doc.isPinned),
      totalLikes: siblings.reduce((sum, doc) => sum + doc.likes, 0),
      totalComments: siblings.reduce((sum, doc) => sum + getAnnouncementCommentCount(doc), 0),
      communityIds,
      // Only meaningful for a real group. With grouping off the entry is deliberately one doc out
      // of several, which is the answer the scoped feed wants - not a group missing its siblings.
      missingSiblingCount: enabled ? Math.max(0, communityIds.length - siblings.length) : 0,
    };
  });

  // Map insertion order follows the order the docs arrived in. That is exactly right when the
  // caller already ordered them (the scheduled tab, by publishAt) and wrong for the published feed,
  // which wants pinned first then recency - so sort explicitly unless asked not to.
  if (preserveOrder) {
    return entries;
  }

  return entries.sort((a, b) => {
    if (a.isPinnedGroup !== b.isPinnedGroup) {
      return a.isPinnedGroup ? -1 : 1;
    }
    return toMillis(b.base) - toMillis(a.base);
  });
};

/**
 * The feed's display entries: apply the community filter, then collapse groups. The list and the
 * Scheduled tab badge both go through here, so the badge can never disagree with the cards it
 * counts.
 * @param {Array} docs
 * @param {Object} options
 * @param {string} options.communityFilter - A community id, or ALL_COMMUNITIES_FILTER.
 * @param {boolean} [options.preserveOrder=false]
 * @returns {Array}
 */
const buildAnnouncementFeed = (docs, { communityFilter, preserveOrder = false } = {}) => {
  const communityDocs = docs.filter((doc) => matchesCommunityFilter(doc, communityFilter));
  // Collapse each multi-community post into one card, but only in the all-communities view.
  // Scoped to a single community, that community's own counts are the answer the coach wants,
  // and an aggregate would report engagement from communities they filtered out.
  return groupAnnouncementsByBasePost(communityDocs, {
    enabled: communityFilter === ALL_COMMUNITIES_FILTER,
    preserveOrder,
  });
};

export {
  ANNOUNCEMENT_DATE_RANGE,
  DEFAULT_ANNOUNCEMENT_DATE_RANGE,
  ANNOUNCEMENT_DATE_RANGE_OPTIONS,
  getSinceDate,
  isRangeCovered,
  mergeAnnouncementDocs,
  buildAnnouncementFeed,
};
